const oauthParameterNames = ['code', 'state', 'access_token', 'id_token', 'refresh_token', 'code_verifier']
  .map(name => [...name].map(char => `(?:${char}|%(?:25)*${char.charCodeAt(0).toString(16)})`).join(''))
  .join('|');
// Match only sensitive assignments, so a surrounding return URL cannot consume a nested callback.
const oauthUrlParameters = new RegExp(`([?&#])(${oauthParameterNames})=([^&#\\s"'<>]*)`, 'gi');
// Capture the encoding depth: separators inside the value are escaped one level further.
const encodedOAuthUrlParameters = new RegExp(
  `(%((?:25)*)(?:3f|23|26))(${oauthParameterNames})%(?:\\2)` +
  `3d((?:(?!%\\2(?:26|23))[^\\s"'<>])*)`, 'gi',
);

function redactOAuthUrlParameters(value: string): string {
  return value
    .replace(oauthUrlParameters, '$1$2=[redacted]')
    .replace(encodedOAuthUrlParameters,
      (_match, separator: string, depth: string, name: string) =>
        `${separator}${name}%${depth}3D[redacted]`);
}

/** Remove bearer media URLs, OAuth callback credentials and private content from diagnostics. */
export function privateMediaTelemetry<T>(event: T): T {
  const seen = new WeakMap<object, unknown>();
  const scrub = (value: unknown, key = ""): unknown => {
    if (
      /^(homeCity|homeCityId|planningPrompt|contentPreview|responsePreview|caption|manifest|mainKey|thumbKey|temporaryKey|finalKey|token|tokenHash|invitationToken|idToken|accessToken|refreshToken|codeVerifier|primaryVerifier|secondaryVerifier|primaryNonce|secondaryNonce)$/i.test(key,
      )
    )
      return "[private-media]";
    if (typeof value === "string") {
      if (/X-Amz-|r2\.cloudflarestorage\.com/i.test(value))
        return "[private-media]";
      return redactOAuthUrlParameters(value)
        .replace(/(\/users\/cities\/?\?)[^\s"'<>]*/gi, '$1[redacted]')
        .replace(/(chatgpt\.com\/?\?)[^\s"'<>]*/gi, '$1[redacted]')
        .replace(/("(?:contentPreview|responsePreview)"\s*:\s*")(?:\\.|[^"\\])*(")/gi, '$1[redacted]$2')
        .replace(/((?:^|[#?&])token=)[^&\s"\']+/gi, '$1[redacted]')
        .replace(/((?:^|%23|%3F|%26)token%3D)[A-Za-z0-9_-]+/gi, '$1[redacted]')
        .replace(/("(?:token|tokenHash|invitationToken)"\s*:\s*")[^"]+(")/gi, '$1[redacted]$2');
    }
    if (!value || typeof value !== "object") return value;
    if (seen.has(value)) return seen.get(value);
    if (value instanceof Date) return value;
    if (value instanceof Error) {
      const error = new Error(scrub(value.message) as string);
      seen.set(value, error);
      error.name = value.name;
      error.stack = scrub(value.stack) as string | undefined;
      for (const [name, item] of Object.entries(value))
        (error as unknown as Record<string, unknown>)[name] = scrub(item, name);
      return error;
    }
    const result: Record<string, unknown> | unknown[] = Array.isArray(value)
      ? []
      : {};
    seen.set(value, result);
    const privateRequest =
      "url" in value &&
      typeof value.url === "string" &&
      /\/users(?:[/?]|$)|\/auth\/links(?:\/|$)|\/our-memories|\/photo-exports|\/partner\/invitations\/resolve|\/partner\/invitation\/[^/]+\/accept/.test(value.url,
      );
    for (const [name, item] of Object.entries(value)) {
      (result as Record<string, unknown>)[name] =
        privateRequest && /^(data|body)$/.test(name)
          ? "[private-media]"
          : scrub(item, name);
    }
    return result;
  };
  return scrub(event) as T;
}
